/**
 * Build Itinerary wizard shell.
 *
 * Owns the step-state machine (see wizardSteps.ts), renders the shared stepper
 * header, the Cancel / Back controls, and the active step's body. The five steps
 * are Event -> Match Teams -> Follow -> Pitchers -> Assign. Step advancement is
 * driven by each step reporting completion (Step 1 on a finished import); the
 * shell does not auto-advance.
 *
 * Two modes:
 * - **Create** (default): a coach imports a schedule at Step 1, which is
 *   confirmed into a hidden draft tournament; finishing publishes the draft.
 * - **Setup** (`?eventId=<copyId>`): a recipient of a distributed copy runs the
 *   post-import steps (2-5) on an already-imported real event. Step 1 is hidden,
 *   the wizard starts at Step 2, Cancel just navigates away (the copy is real,
 *   not a throwaway draft), and finishing calls complete-setup instead of
 *   publishing.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../../../hooks/useAuth';
import { WizardStepper } from './WizardStepper';
import { EventStep, type EventStepResult } from './EventStep';
import { ImportOverview } from './ImportOverview';
import { ScheduleIngestionModal } from '../ScheduleIngestionModal';
import { MatchTeamsStep } from './MatchTeamsStep';
import { FollowPlayersStep } from './FollowPlayersStep';
import { PitchersStep } from './PitchersStep';
import { AssignStep } from './AssignStep';
import { buildAutoTeamResolutions, tournamentTitle } from './confirmBridge';
import {
  getScheduleIngestionReview,
  confirmScheduleIngestion,
} from '../../../../../services/schedule-ingestions';
import {
  publishTournament,
  completeTournamentSetup,
  getTournamentGames,
} from '../../../../../services/tournament-games';
import { deleteEvent, getEvent } from '../../../../../services/calendar';
import { getSafeErrorMessage } from '../../../../../utils/errorHandler';
import {
  loadWizardProgress,
  saveWizardProgress,
  clearWizardProgress,
} from './wizardProgress';
import {
  completeAndAdvance,
  goBack,
  goToStep,
  canGoBack,
  initialWizardState,
  LAST_STEP,
  type WizardState,
  type WizardStepId,
} from './wizardSteps';
import { Button } from '../../../../../components/Button';
import { ShareWithProgramStep } from './ShareWithProgramStep';
import { ShareWiderPanel } from './ShareWiderPanel';
import { isSafeScheduleUrl } from '../../../../admin/scheduleLink';
import {
  readRequestLaunch,
  type ItineraryRequestLaunch,
} from '../itinerarySetup';
import './BuildItineraryWizard.css';

/** The step a setup-mode recipient starts on (Step 1 import is already done). */
const SETUP_START_STEP: WizardStepId = 2;

export function BuildItineraryWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { effectiveOrganizationId, isSystemAdmin } = useAuth();

  // Setup mode: launched for an existing distributed copy via ?eventId=<copyId>.
  // The id is read once on mount so it stays stable across renders.
  const setupEventIdRef = useRef<string | null>(searchParams.get('eventId'));
  const setupEventId = setupEventIdRef.current;
  const isSetupMode = !!setupEventId;

  // Request mode: RosterPilot staff building an itinerary a program asked for, launched from the
  // admin queue. Read once on mount for the same reason as the setup id. This is CREATE mode with
  // context attached — the tournament does not exist yet, which is why there is a request at all —
  // so it changes no step, only what step 1 is prefilled with and what happens after Finish.
  const requestLaunchRef = useRef<ItineraryRequestLaunch | null>(
    readRequestLaunch(searchParams),
  );
  // system_admin only, and not because the params are secret — they come from an admin-only page.
  // Distribution is system-admin-gated server-side, so showing a coach who hand-typed this URL a
  // share step would show them a button that can only 403. Without it they get the ordinary create
  // flow, which is the right thing to degrade to.
  const requestLaunch = isSetupMode || !isSystemAdmin ? null : requestLaunchRef.current;

  const [state, setState] = useState<WizardState>(() =>
    initialWizardState(isSetupMode ? SETUP_START_STEP : undefined),
  );
  // The details collected at Step 1 (create) or seeded from the copy (setup),
  // kept so later slices (players/review) can pick up the tournament.
  const [eventResult, setEventResult] = useState<EventStepResult | null>(null);
  // Setup mode: a persistent "Re-import itinerary" action that re-imports/merges a
  // new schedule into THIS event. reloadKey remounts the step body after a merge so
  // the refreshed games show.
  const [showReimport, setShowReimport] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // The confirm bridge: turning the extracted ingestion into a real tournament
  // (CalendarEvent + games) so later steps have a calendarEventId. Runs on the
  // Step 1 -> Step 2 transition (create only); blocks advancement until it resolves.
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  // Setup mode surfaces a load error if the seeded copy can't be fetched.
  const [setupError, setSetupError] = useState('');
  // True when we restored an in-progress itinerary on load (shows a resume banner).
  const [resumed, setResumed] = useState(false);
  // Request mode only: the terminal share screen, shown once the tournament is published. It is
  // deliberately NOT a sixth entry in the step machine — that union is fixed at five and is ported
  // verbatim to mobile, which has no admin queue and so can never reach this screen.
  const [shareState, setShareState] = useState<{ eventId: string; name: string } | null>(null);
  const restoredRef = useRef(false);

  // Setup mode: seed eventResult from the distributed copy so Steps 2-5 have a
  // tournament to work against. The `active` flag alone makes this safe under
  // React StrictMode's double-invoke (a ref guard here would let the first,
  // discarded run mark itself done and starve the second real run, leaving
  // eventResult null and stranding Step 2 on its "no tournament" guard). getEvent
  // is idempotent, so a duplicate fetch in dev is harmless.
  useEffect(() => {
    if (!isSetupMode || !setupEventId) return;

    let active = true;
    getEvent(setupEventId)
      .then((event) => {
        if (!active) return;
        // ingestionId and the dates are intentionally blank: setup mode never
        // re-confirms an ingestion (the copy is already a real event), and steps
        // 2-5 only read calendarEventId + eventName.
        setEventResult({
          ingestionId: '',
          calendarEventId: event.id,
          eventName: event.title,
          startDate: '',
          endDate: '',
        });
      })
      .catch(() => {
        if (active) {
          setSetupError(
            "We couldn't open this schedule to set up. Please go back and try again.",
          );
        }
      });
    return () => {
      active = false;
    };
  }, [isSetupMode, setupEventId]);

  // Resume: on load, if saved progress points at a draft tournament that still exists, jump back to
  // where the coach left off. Runs once (waits for the org to resolve). A missing/deleted draft
  // clears the stale progress. Setup mode never resumes from create-mode progress.
  //
  // NEITHER DOES REQUEST MODE, and that exclusion is a tenancy guard rather than a preference.
  // Saved progress is ONE slot keyed by organization id alone (see wizardProgress.ts) and carries
  // nothing tying it to a request — so building from a queue row would restore whatever draft was
  // last abandoned in this org, and resume lands on step 2+, past the step the request's name
  // seeds. The share step would then publish that unrelated draft, deliver it to the program that
  // asked for something else — with its notes, under StaffDistribution — and close their row BY ID,
  // so not even a title mismatch would give it away. Two queue rows open in two tabs is enough to
  // hit it, which is exactly how the Build link is meant to be used.
  useEffect(() => {
    if (isSetupMode || requestLaunchRef.current || restoredRef.current || !effectiveOrganizationId) {
      return;
    }
    restoredRef.current = true;

    const progress = loadWizardProgress(effectiveOrganizationId);
    if (!progress) return;

    let active = true;
    getTournamentGames(progress.calendarEventId)
      .then(() => {
        if (!active) return;
        setEventResult({
          ingestionId: '',
          calendarEventId: progress.calendarEventId,
          eventName: progress.eventName,
          startDate: '',
          endDate: '',
        });
        // Clamp a restored step to the current range so an old saved step
        // (from a prior flow) can't land out of bounds.
        const restoredStep = Math.min(progress.currentStep, LAST_STEP) as WizardStepId;
        setState({
          currentStep: restoredStep,
          highestCompletedStep: restoredStep - 1,
          minStep: 1,
        });
        setResumed(true);
      })
      .catch(() => {
        // The draft is gone (published/deleted/expired) — don't resume a ghost.
        if (active) clearWizardProgress(effectiveOrganizationId);
      });

    return () => {
      active = false;
    };
  }, [isSetupMode, effectiveOrganizationId]);

  // Persist progress once there's a confirmed (draft) tournament, so a refresh
  // can resume. Create mode only — setup mode works on a real copy that lives on
  // the server, and must not clobber a create-mode resume.
  useEffect(() => {
    if (isSetupMode) return;
    const calendarEventId = eventResult?.calendarEventId;
    if (!calendarEventId || state.currentStep < 2) return;
    saveWizardProgress(effectiveOrganizationId, {
      calendarEventId,
      eventName: eventResult?.eventName ?? '',
      currentStep: state.currentStep,
    });
  }, [isSetupMode, eventResult, state.currentStep, effectiveOrganizationId]);

  // Leaving the wizard without finishing. In create mode the tournament was
  // created as a hidden draft, so discard it (best-effort). In setup mode the
  // copy is real (already distributed) — just navigate away, leaving it in its
  // needs-setup state for next time.
  const handleCancel = () => {
    if (!isSetupMode) {
      const draftId = eventResult?.calendarEventId;
      if (draftId) {
        void deleteEvent(draftId).catch(() => {
          /* best-effort; the draft stays hidden even if this fails */
        });
      }
      clearWizardProgress(effectiveOrganizationId);
    }
    navigate('/schedule');
  };

  // Final step done. Create mode: publish the draft tournament (with the
  // possibly-renamed title) so it appears on the calendar. Setup mode: mark the
  // distributed copy set up (clears needs-setup) so it settles on the calendar.
  //
  // Throws on a failed finalize call (does NOT navigate) so AssignStep keeps the
  // user on the step with the error shown, rather than silently navigating away
  // as if it succeeded.
  const handleFinish = async (finalName?: string) => {
    const eventId = eventResult?.calendarEventId;
    if (eventId) {
      if (isSetupMode) {
        await completeTournamentSetup(eventId);
      } else {
        await publishTournament(eventId, finalName);
      }
    }
    if (!isSetupMode) clearWizardProgress(effectiveOrganizationId);

    // Hold here and offer to share, rather than dropping staff on the schedule with the delivery
    // still to do and nothing saying so. Publishing has already happened, so backing out of the
    // share loses nothing but the delivery.
    //
    // `isSystemAdmin`, because both share mechanisms are system_admin-only server-side and create
    // mode is an ordinary coach's flow too — a coach who imported their own schedule must not be
    // held on a screen whose every action can only 403. `!isSetupMode` because a recipient setting
    // up a copy someone sent them is not its owner and has nothing to share.
    if (eventId && !isSetupMode && isSystemAdmin) {
      setShareState({
        eventId,
        name: finalName || eventResult?.eventName || requestLaunch?.tournamentName || 'This itinerary',
      });
      return;
    }

    navigate('/schedule');
  };

  // Abandon the resumed draft and start a fresh import (stays in the wizard).
  // Create mode only (setup mode has no resume banner).
  const handleStartOver = () => {
    const draftId = eventResult?.calendarEventId;
    if (draftId) {
      void deleteEvent(draftId).catch(() => {
        /* best-effort */
      });
    }
    clearWizardProgress(effectiveOrganizationId);
    setEventResult(null);
    setResumed(false);
    setState(initialWizardState());
  };

  /**
   * Confirm the just-extracted ingestion into a tournament, then advance. Team
   * matches are auto-resolved (confident match -> link, else create) and are
   * user-correctable later. If the ingestion was already confirmed (review
   * returns a resultCalendarEventId), reuse it instead of re-confirming.
   */
  const handleEventCompleted = async (result: EventStepResult) => {
    if (confirming) return;
    setConfirming(true);
    setConfirmError('');
    try {
      const review = await getScheduleIngestionReview(result.ingestionId);

      let calendarEventId = review.resultCalendarEventId ?? undefined;
      if (!calendarEventId) {
        const confirmed = await confirmScheduleIngestion(result.ingestionId, {
          // Prefer the coach's typed name; else the name auto-detected from the link; else a default.
          title: tournamentTitle(result.eventName || review.suggestedName || ''),
          // Only send explicit dates when the user set them; otherwise omit so the
          // server derives the tournament's range from the imported games' dates.
          ...(result.startsAtUtc && result.endsAtUtc
            ? { startsAtUtc: result.startsAtUtc, endsAtUtc: result.endsAtUtc }
            : {}),
          teamResolutions: buildAutoTeamResolutions(review.teamMatches),
          // Create hidden — it only lands on the calendar when the wizard finishes.
          draft: true,
        });
        calendarEventId = confirmed.calendarEventId;
      }

      // Don't jump to Teams: hold on Step 1 and show the import overview so the coach can review
      // the event (name, dates, venues, game count) first. The overview's Next advances.
      setEventResult({ ...result, calendarEventId });
    } catch (err) {
      setConfirmError(
        getSafeErrorMessage(
          err,
          "We couldn't build the tournament from that schedule. Please try again."
        )
      );
    } finally {
      setConfirming(false);
    }
  };

  const handleStepSelect = (step: WizardStepId) => {
    setState((prev) => goToStep(step, prev));
  };

  const advance = () => setState((prev) => completeAndAdvance(prev));
  const goBackNav = () => setState((prev) => goBack(prev));
  const backEnabled = canGoBack(state);

  const renderStepBody = () => {
    switch (state.currentStep) {
      case 1:
        // After import the draft tournament exists: pause on the review overview instead of the
        // upload form, so the coach confirms the event before matching teams.
        return eventResult?.calendarEventId ? (
          <ImportOverview
            calendarEventId={eventResult.calendarEventId}
            eventName={eventResult.eventName}
            onNext={advance}
            onBack={goBackNav}
            canGoBack={backEnabled}
            onRenamed={(name) =>
              setEventResult((prev) => (prev ? { ...prev, eventName: name } : prev))
            }
          />
        ) : (
          <EventStep
            onCompleted={handleEventCompleted}
            defaultEventName={requestLaunch?.tournamentName}
          />
        );
      case 2:
        return (
          <MatchTeamsStep
            calendarEventId={eventResult?.calendarEventId}
            onNext={advance}
            onBack={goBackNav}
            canGoBack={backEnabled}
          />
        );
      case 3:
        return (
          <FollowPlayersStep
            calendarEventId={eventResult?.calendarEventId}
            onNext={advance}
            onBack={goBackNav}
            canGoBack={backEnabled}
          />
        );
      case 4:
        return (
          <PitchersStep
            calendarEventId={eventResult?.calendarEventId}
            onNext={advance}
            onBack={goBackNav}
            canGoBack={backEnabled}
          />
        );
      case 5:
        return (
          <AssignStep
            calendarEventId={eventResult?.calendarEventId}
            onFinish={handleFinish}
            onBack={goBackNav}
            canGoBack={backEnabled}
          />
        );
      default:
        return null;
    }
  };

  // Published: the wizard's work is done and the only thing left is delivery, so the stepper and
  // its steps come down rather than sitting there re-offering work that is already committed.
  //
  // This used to require `requestLaunch` as well, which meant an itinerary built in ADVANCE — the
  // whole point of the catalog — finished with no way to share it at all. Staff had to go find the
  // tournament again on the schedule and scroll to the admin block, which is the same disconnect
  // ShareWithProgramStep exists to close. Sharing is now offered either way; only the framing
  // differs.
  if (shareState) {
    return (
      <div className="wiz-page">
        <div className="wiz-topline">
          <div>
            <h1 className="wiz-h1">Build Itinerary</h1>
            <p className="wiz-sub">
              {requestLaunch
                ? 'One step left: send it to the program that asked for it.'
                : 'Built and on your calendar. Share it with other programs, or keep it to yourself.'}
            </p>
          </div>
        </div>

        {requestLaunch && (
          <ShareWithProgramStep
            calendarEventId={shareState.eventId}
            tournamentName={shareState.name}
            launch={requestLaunch}
            onSkip={() => navigate('/schedule')}
            onShared={() => navigate('/admin/itinerary-requests')}
          />
        )}

        {/*
          Sharing wider is offered in BOTH cases and is not an alternative to the request share
          above: fulfilling one program's request and putting the same tournament in front of
          everyone else are separate decisions, and staff who have just built it are the only
          people in a position to make the second one.
        */}
        <ShareWiderPanel
          calendarEventId={shareState.eventId}
          heading={requestLaunch ? 'Share it more widely' : 'Share this itinerary'}
          lede={
            requestLaunch
              ? `Optional, and separate from the request above. Publish ${shareState.name} to the catalog so any program can find it, or send it straight to others.`
              : `Publish ${shareState.name} to the catalog so any program can find and add it, or send a copy straight onto chosen programs' calendars.`
          }
        />

        {!requestLaunch && (
          <div className="wiz-share__actions">
            <Button type="button" variant="secondary" onClick={() => navigate('/schedule')}>
              Done
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="wiz-page">
      <div className="wiz-topline">
        <div>
          <h1 className="wiz-h1">{isSetupMode ? 'Set Up Itinerary' : 'Build Itinerary'}</h1>
          <p className="wiz-sub">
            {isSetupMode
              ? 'Match this shared schedule to your teams, follow players, and assign staff.'
              : 'Create a personalized schedule based on your starred players.'}
          </p>
        </div>
        <div className="wiz-topline__actions">
          {isSetupMode && (
            <button
              type="button"
              className="wiz-btn"
              onClick={() => setShowReimport(true)}
              data-testid="wizard-reimport"
            >
              🔄 Re-import itinerary
            </button>
          )}
          <button
            type="button"
            className="wiz-btn"
            onClick={handleCancel}
            data-testid="wizard-cancel"
          >
            Cancel
          </button>
        </div>
      </div>

      {requestLaunch && (
        <div className="wiz-request" data-testid="wizard-request-banner">
          <span className="wiz-request__org">{requestLaunch.organizationName}</span>
          <span className="wiz-request__what">
            asked for <strong>{requestLaunch.tournamentName}</strong>
          </span>
          {/*
              The program's own schedule link, carried across from the queue row. Step 1 imports a
              PDF or an image, not a URL, so this cannot prefill the import — but it is the page
              staff need open to get one, and re-finding it in the queue was a tab-hunt every time.
          */}
          {requestLaunch.scheduleUrl && isSafeScheduleUrl(requestLaunch.scheduleUrl) && (
            <a
              className="wiz-request__link"
              href={requestLaunch.scheduleUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="wizard-request-schedule-link"
            >
              ↗ Open their schedule link
            </a>
          )}
        </div>
      )}

      <WizardStepper state={state} onStepSelect={handleStepSelect} />

      {setupError && (
        <div className="wiz-error-box" data-testid="wizard-setup-error">
          <p role="alert">{setupError}</p>
        </div>
      )}

      {resumed && (
        <div className="wiz-note wiz-note--tight" data-testid="wizard-resumed">
          <span className="wiz-note__icon" aria-hidden="true">
            ↻
          </span>
          <div>
            Resumed your in-progress itinerary.{' '}
            <button
              type="button"
              className="wiz-link-btn"
              onClick={handleStartOver}
              data-testid="wizard-start-over"
            >
              Start over
            </button>
          </div>
        </div>
      )}

      {eventResult && state.currentStep > 1 && (
        <div className="wiz-event-banner" data-testid="wizard-event-banner">
          <b>{eventResult.eventName || 'Imported event'}</b>
          <span className="wiz-sub"> · schedule imported</span>
        </div>
      )}

      {confirming && (
        <div className="wiz-note" data-testid="wizard-confirming">
          <span className="wiz-spinner" aria-hidden="true" />
          <div>Building your tournament from the schedule...</div>
        </div>
      )}
      {confirmError && (
        <div className="wiz-error-box" data-testid="wizard-confirm-error">
          <p role="alert">{confirmError}</p>
        </div>
      )}

      <div className="wiz-body" key={reloadKey}>{renderStepBody()}</div>

      {showReimport && setupEventId && (
        <ScheduleIngestionModal
          onClose={() => setShowReimport(false)}
          defaultTitle={eventResult?.eventName ?? ''}
          targetCalendarEventId={setupEventId}
          onConfirmed={() => {
            setShowReimport(false);
            // Remount the current step so its games refetch after the merge.
            setReloadKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}
